/**
 * FASWORLD header — sticky chrome, desktop mega menus, mobile accordion drawer
 */
(() => {
  const FOCUSABLE =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  const BACKGROUND_INERT_TARGETS = '#header-group > *, main, #MainContent, #footer-group, footer';

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = () => window.matchMedia('(hover: hover)').matches;

  const initHeader = (header) => {
    if (!(header instanceof HTMLElement) || header.dataset.fwHeaderReady === 'true') return;
    header.dataset.fwHeaderReady = 'true';

    const section = header.closest('.fw-header-section') || header;
    const stickyBehavior = header.dataset.stickyBehavior || 'scroll-up';
    const drawer = header.querySelector('[data-fw-drawer]');
    const openBtn = header.querySelector('[data-fw-drawer-open]');
    const closeBtn = header.querySelector('[data-fw-drawer-close]');
    const triggers = [...header.querySelectorAll('[data-fw-mega-trigger]')];
    const panels = [...header.querySelectorAll('[data-fw-mega-panel]')];
    const parents = [...header.querySelectorAll('[data-fw-mega-parent]')];

    let lastY = window.scrollY;
    let ticking = false;
    let drawerHome = null;
    let lastFocus = null;
    let focusTrapHandler = null;
    let drawerClosing = false;
    let megaCloseTimer = null;
    let activeMegaTrigger = null;
    let backgroundInertState = null;
    const DRAWER_MS = 300;
    const MEGA_CLOSE_MS = 140;
    const cleanups = [];

    const on = (target, type, handler, options) => {
      if (!target) return;
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    };

    const isHiddenByClosedDetails = (el) => {
      let node = el.parentElement;
      while (node) {
        if (node instanceof HTMLDetailsElement && !node.open) {
          const summary = node.querySelector(':scope > summary');
          if (!(summary instanceof HTMLElement && summary.contains(el))) return true;
        }
        node = node.parentElement;
      }
      return false;
    };

    const isFocusBlocked = (el) => {
      if (!(el instanceof HTMLElement)) return true;
      if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') return true;
      if (el.hasAttribute('hidden') || el.hasAttribute('inert')) return true;
      if (el.closest('[hidden], [inert]')) return true;
      // Closed <details> keep descendants in the DOM; only the summary remains interactive.
      if (isHiddenByClosedDetails(el)) return true;
      if (el.getClientRects().length === 0) return true;
      return false;
    };

    const getFocusableIn = (root) => {
      if (!(root instanceof HTMLElement)) return [];
      return [...root.querySelectorAll(FOCUSABLE)].filter((el) => !isFocusBlocked(el));
    };

    const getFocusable = () => getFocusableIn(drawer);

    const setHeightVars = () => {
      const headerGroup = document.querySelector('#header-group');
      const headerHeight = header.offsetHeight || 0;
      let groupHeight = headerHeight;

      if (headerGroup) {
        groupHeight = 0;
        [...headerGroup.children].forEach((child) => {
          if (child instanceof HTMLElement) groupHeight += child.offsetHeight;
        });
        if (groupHeight <= 0) groupHeight = headerHeight;
      }

      document.body.style.setProperty('--header-height', `${headerHeight}px`);
      document.body.style.setProperty('--header-group-height', `${groupHeight || headerHeight}px`);
      document.body.style.setProperty('--transparent-header-offset-boolean', '0');
      setStickyOffset();
    };

    const isDrawerOpen = () =>
      Boolean(drawer && !drawer.hasAttribute('hidden') && drawer.classList.contains('is-open'));

    const isMegaOpen = () => triggers.some((t) => t.getAttribute('aria-expanded') === 'true');

    const isPinned = () => {
      if (header.matches(':focus-within')) return true;
      if (isDrawerOpen()) return true;
      if (isMegaOpen()) return true;
      if (document.querySelector('dialog[open]')) return true;
      if (document.querySelector('[popover]:popover-open')) return true;
      if (document.documentElement.classList.contains('shopify-design-mode')) return true;
      return false;
    };

    const setStickyOffset = () => {
      const hidden = header.dataset.fwHeaderState === 'hidden';
      const height = section.offsetHeight || header.offsetHeight || 0;
      document.body.style.setProperty('--fw-sticky-offset', `${hidden ? 0 : height}px`);
    };

    const setState = (state) => {
      if (header.dataset.fwHeaderState === state) return;
      header.dataset.fwHeaderState = state;
      setStickyOffset();
    };

    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      header.classList.toggle('is-scrolled', y > 8);

      if (stickyBehavior !== 'scroll-up' || reduceMotion()) {
        setState('visible');
        lastY = y;
        return;
      }

      if (isPinned() || y < 48) {
        setState('visible');
        lastY = y;
        return;
      }

      const delta = y - lastY;
      if (Math.abs(delta) < 6) return;
      setState(delta > 0 ? 'hidden' : 'visible');
      lastY = y;
    };

    const requestScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        onScroll();
        ticking = false;
      });
    };

    const clearMegaClose = () => {
      if (megaCloseTimer == null) return;
      window.clearTimeout(megaCloseTimer);
      megaCloseTimer = null;
    };

    const closeMega = ({ restoreFocus = false } = {}) => {
      clearMegaClose();
      const triggerToRestore = activeMegaTrigger;
      triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      panels.forEach((panel) => {
        panel.hidden = true;
        panel.setAttribute('inert', '');
      });
      activeMegaTrigger = null;
      if (restoreFocus && triggerToRestore instanceof HTMLElement) {
        triggerToRestore.focus();
      }
    };

    const scheduleMegaClose = () => {
      if (!canHover()) return;
      clearMegaClose();
      megaCloseTimer = window.setTimeout(() => {
        megaCloseTimer = null;
        closeMega();
      }, MEGA_CLOSE_MS);
    };

    const openMega = (trigger) => {
      const id = trigger.getAttribute('aria-controls');
      const panel = id ? document.getElementById(id) : null;
      if (!panel) return;

      clearMegaClose();
      triggers.forEach((t) => {
        if (t !== trigger) t.setAttribute('aria-expanded', 'false');
      });
      panels.forEach((item) => {
        if (item === panel) return;
        item.hidden = true;
        item.setAttribute('inert', '');
      });

      activeMegaTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      panel.removeAttribute('inert');
      setState('visible');
    };

    parents.forEach((parent) => {
      const trigger = parent.querySelector('[data-fw-mega-trigger]');
      if (!(trigger instanceof HTMLElement)) return;

      on(parent, 'mouseenter', () => {
        if (canHover()) openMega(trigger);
      });
      on(parent, 'mouseleave', scheduleMegaClose);

      // Coarse / no-hover (tablet at 990+): first tap opens mega; second tap follows link.
      const parentLink = parent.querySelector('.fw-header-menu__link--parent-top');
      if (parentLink instanceof HTMLAnchorElement) {
        on(parentLink, 'click', (event) => {
          if (canHover()) return;
          if (trigger.getAttribute('aria-expanded') === 'true') return;
          event.preventDefault();
          openMega(trigger);
        });
      }
    });

    panels.forEach((panel) => {
      on(panel, 'mouseenter', clearMegaClose);
      on(panel, 'mouseleave', scheduleMegaClose);
    });

    triggers.forEach((trigger, index) => {
      on(trigger, 'click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const open = trigger.getAttribute('aria-expanded') === 'true';
        if (open) closeMega();
        else openMega(trigger);
      });

      on(trigger, 'keydown', (event) => {
        const rtl = document.documentElement.getAttribute('dir') === 'rtl';
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          const next = rtl ? index - 1 : index + 1;
          triggers[(next + triggers.length) % triggers.length]?.focus();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          const next = rtl ? index + 1 : index - 1;
          triggers[(next + triggers.length) % triggers.length]?.focus();
        } else if (event.key === 'ArrowDown') {
          event.preventDefault();
          openMega(trigger);
          const panel = document.getElementById(trigger.getAttribute('aria-controls') || '');
          getFocusableIn(panel)[0]?.focus();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          closeMega({ restoreFocus: true });
        }
      });
    });

    const detachFocusTrap = () => {
      if (!focusTrapHandler) return;
      document.removeEventListener('keydown', focusTrapHandler, true);
      focusTrapHandler = null;
    };

    const attachFocusTrap = () => {
      detachFocusTrap();
      focusTrapHandler = (event) => {
        if (event.key !== 'Tab' || !isDrawerOpen()) return;
        const nodes = getFocusable();
        if (!nodes.length) {
          event.preventDefault();
          return;
        }
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', focusTrapHandler, true);
    };

    const setBackgroundInert = (enabled) => {
      const targets = [...document.querySelectorAll(BACKGROUND_INERT_TARGETS)].filter(
        (el) => el instanceof HTMLElement && el !== drawer && !drawer?.contains(el)
      );

      if (enabled) {
        backgroundInertState = new Map();
        targets.forEach((el) => {
          backgroundInertState.set(el, el.hasAttribute('inert'));
          el.setAttribute('inert', '');
        });
        return;
      }

      if (backgroundInertState instanceof Map) {
        backgroundInertState.forEach((wasInert, el) => {
          if (!(el instanceof HTMLElement)) return;
          if (wasInert) el.setAttribute('inert', '');
          else el.removeAttribute('inert');
        });
        backgroundInertState = null;
        return;
      }

      targets.forEach((el) => el.removeAttribute('inert'));
    };

    const resetMobileMenu = () => {
      drawer?.querySelectorAll('.fw-mobile-menu__details[open]').forEach((details) => {
        details.removeAttribute('open');
      });
    };

    const openDrawer = () => {
      if (!drawer || !openBtn || drawerClosing) return;
      closeMega();
      lastFocus = document.activeElement;
      resetMobileMenu();

      if (!drawerHome) drawerHome = drawer.parentElement;
      if (drawer.parentElement !== document.body) {
        document.body.appendChild(drawer);
      }

      document.documentElement.classList.add('fw-drawer-open');
      section.classList.add('fw-header-section--drawer-open');
      drawer.hidden = false;
      drawer.classList.remove('is-closing');
      drawer.removeAttribute('inert');
      openBtn.setAttribute('aria-expanded', 'true');
      setBackgroundInert(true);
      setState('visible');

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          drawer.classList.add('is-open');
        });
      });

      attachFocusTrap();
      (closeBtn || getFocusable()[0])?.focus();
    };

    const closeDrawer = () => {
      if (!drawer || !openBtn || drawerClosing) return;
      if (drawer.hidden && !drawer.classList.contains('is-open')) return;

      drawerClosing = true;
      drawer.classList.remove('is-open');
      drawer.classList.add('is-closing');
      drawer.setAttribute('inert', '');
      openBtn.setAttribute('aria-expanded', 'false');
      detachFocusTrap();

      const finish = () => {
        drawer.hidden = true;
        drawer.classList.remove('is-closing');
        document.documentElement.classList.remove('fw-drawer-open');
        section.classList.remove('fw-header-section--drawer-open');
        setBackgroundInert(false);
        drawerClosing = false;
        resetMobileMenu();

        if (drawerHome && drawer.parentElement !== drawerHome) {
          drawerHome.appendChild(drawer);
        }

        (lastFocus || openBtn)?.focus();
        lastFocus = null;
      };

      const panel = drawer.querySelector('.fw-header__drawer-panel');
      if (reduceMotion() || !panel) {
        finish();
        return;
      }

      let done = false;
      const onEnd = (event) => {
        if (event.target !== panel || event.propertyName !== 'transform') return;
        if (done) return;
        done = true;
        panel.removeEventListener('transitionend', onEnd);
        finish();
      };
      panel.addEventListener('transitionend', onEnd);
      window.setTimeout(() => {
        if (done) return;
        done = true;
        panel.removeEventListener('transitionend', onEnd);
        finish();
      }, DRAWER_MS + 80);
    };

    on(openBtn, 'click', openDrawer);
    on(closeBtn, 'click', closeDrawer);
    on(drawer, 'click', (event) => {
      if (event.target === drawer) closeDrawer();
    });

    on(document, 'keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (isDrawerOpen()) {
        event.preventDefault();
        closeDrawer();
      } else if (isMegaOpen()) {
        event.preventDefault();
        closeMega({ restoreFocus: true });
      }
    });

    on(document, 'click', (event) => {
      const inHeader = header.contains(event.target);
      const inDrawer = drawer && drawer.contains(event.target);
      if (!inHeader && !inDrawer) closeMega();
    });

    on(window, 'scroll', requestScroll, { passive: true });
    on(window, 'resize', setHeightVars);

    let resizeObserver = null;
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(setHeightVars);
      resizeObserver.observe(header);
      cleanups.push(() => resizeObserver?.disconnect());
    }

    section.setAttribute('data-fw-header-shell', '');
    setHeightVars();
    onScroll();
    resetMobileMenu();

    const forceCloseDrawer = () => {
      if (!drawer) return;
      drawerClosing = false;
      detachFocusTrap();
      drawer.classList.remove('is-open', 'is-closing');
      drawer.hidden = true;
      drawer.setAttribute('inert', '');
      openBtn?.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('fw-drawer-open');
      section.classList.remove('fw-header-section--drawer-open');
      setBackgroundInert(false);
      resetMobileMenu();

      if (drawerHome?.isConnected && drawer.parentElement !== drawerHome) {
        drawerHome.appendChild(drawer);
      } else if (!drawerHome?.isConnected && drawer.parentElement === document.body) {
        drawer.remove();
      }
    };

    header.__fwHeaderDestroy = () => {
      closeMega();
      forceCloseDrawer();
      cleanups.forEach((fn) => fn());
      delete header.dataset.fwHeaderReady;
      delete header.__fwHeaderDestroy;
    };
  };

  const boot = () => {
    document.querySelectorAll('[data-fw-header]').forEach((header) => initHeader(header));
  };

  boot();

  document.addEventListener('shopify:section:load', (event) => {
    const header = event.target?.querySelector?.('[data-fw-header]') || event.target;
    if (header?.matches?.('[data-fw-header]')) initHeader(header);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const header = event.target?.querySelector?.('[data-fw-header]');
    header?.__fwHeaderDestroy?.();
  });
})();
